import { IEvent, IExclude } from "@lib/store/src/calendar";
import { IDateEntryFormat } from "@lib/types";
import { Moment } from "moment";

/**
 * Список дней для отработки исключений (Тип 0: Date Entry)
 * @param {Moment} date Текущая дата каледнаря
 * @param {IDateEntryFormat} start Время начала исключения
 * @returns {number[]} Список дней
 */
export function getDaysInDate(
  date: Moment,
  start: IDateEntryFormat
): number[] {
  const now = date.clone();
  const currentMonth = date.clone().month() + 1;

  const startMonth = start?.month === "**" ? currentMonth : start?.month;
  const startYear = start?.year === "****" ? now.year() : start?.year;

  if (checkSpecificMonth(startMonth, currentMonth)) return [];
  if (checkSpecificYear(startYear, now.year())) return [];

  let days: number[] = [];

  if (start.day === "**") {
    // Любой день месяца
    days = Array.from({ length: date.daysInMonth() }, (_, i) => i + 1);
  } else if (start.day === 32) {
    // Последний день месяца
    days = [date.clone().endOf("month").date()];
  } else if (start.day === 33) {
    // Нечетные дни
    days = Array.from({ length: date.daysInMonth() }, (_, i) => i + 1).filter(
      (day) => day % 2 !== 0
    );
  } else if (start.day === 34) {
    // Четные дни
    days = Array.from({ length: date.daysInMonth() }, (_, i) => i + 1).filter(
      (day) => day % 2 === 0
    );
  } else {
    // Конкретный день
    days = [parseInt(start.day.toString())];
  }

  // Фильтрация по дню недели
  if (start.weekDay !== "*") {
    days = days.filter(
      (day) => date.clone().date(day).isoWeekday() === start.weekDay
    );
  }

  return days;
}

/**
 * Список дней в диапазоне для отработки исключений (Date Range Entry Format: 1,dDDMMYYYY,dDDMMYYYY)
 * @param {Moment} date Текущая дата календаря
 * @param {IDateEntryFormat} start Время начала исключения
 * @param {IDateEntryFormat} end Время окончания исключения
 * @returns {number[]} Список дней
 */
export function getDaysInDateRange(
  date: Moment,
  start: IDateEntryFormat,
  end: IDateEntryFormat
): number[] {
  const now = date.clone();

  // Проверка года и месяца
  if (!isDateInRange(now, start, end)) return [];

  // Получаем дни согласно диапазону
  const days = getDaysForRange(now, start, end);

  // Фильтрация по дню недели
  return filterByWeekDay(days, now, start, end);
}

function isDateInRange(date: Moment, start: IDateEntryFormat, end: IDateEntryFormat): boolean {
  const currentMonth = date.month() + 1;
  const currentYear = date.year();

  // Проверка года
  const startYear = start.year === "****" ? 1900 : parseInt(start.year.toString());
  const endYear = end.year === "****" ? 2255 : parseInt(end.year.toString());
  
  if (currentYear < startYear || currentYear > endYear) return false;
   
  // Проверка месяца
  const startMonth = start.month === "**" ? 1 : parseInt(start.month.toString());
  const endMonth = end.month === "**" ? 12 : parseInt(end.month.toString());

  return isMonthInRange(currentMonth, startMonth, endMonth);
}

function isMonthInRange(month: number, startMonth: number, endMonth: number): boolean {
  if (startMonth <= endMonth) {
    return month >= startMonth && month <= endMonth;
  }
  return month >= startMonth || month <= endMonth;
}

function getDaysForRange(date: Moment, start: IDateEntryFormat, end: IDateEntryFormat): number[] {
  const daysInMonth = date.daysInMonth();

  // Определяем границы дней. По умолчанию для **
  let fromDay = start.day === "**" ? 1 : getEffectiveDay(start.day, date);
  let toDay = end.day === "**" || end.day === 32 ? daysInMonth : parseInt(end.day.toString());

  // Генерируем дни
  const days: number[] = [];
  for (let day = fromDay; day <= toDay; day++) {
    // Проверяем тип дня, если указан
    if (start.day === 33 || end.day === 33) {
      if (day % 2 !== 1) continue;
    } else if (start.day === 34 || end.day === 34) {
      if (day % 2 !== 0) continue;
    }
    
    days.push(day);
  }

  return days;
}

function getEffectiveDay(day: string | number, date: Moment): number {
  if (day === 32) return date.daysInMonth();
  if (day === 33) return 1;
  if (day === 34) return 2;
  return parseInt(day.toString());
}

function filterByWeekDay(days: number[], date: Moment, start: IDateEntryFormat, end: IDateEntryFormat): number[] {
  if (start.weekDay === "*" && end.weekDay === "*") return days;

  const startWeekDay = start.weekDay === "*" ? 1 : parseInt(start.weekDay.toString());
  const endWeekDay = end.weekDay === "*" ? 7 : parseInt(end.weekDay.toString());

  return days.filter(day => {
    const weekDay = date.clone().date(day).isoWeekday();

    // Для диапазона, переходящего через неделю (например, Чт-Вт)
    if (startWeekDay > endWeekDay) {
        return weekDay >= startWeekDay || weekDay <= endWeekDay;
    }
    // Для обычного диапазона (например, Пн-Пт)
    return weekDay >= startWeekDay && weekDay <= endWeekDay;
  }); 
}
 

/**
 * Список дней для отработки исключений (Тип 2: Week And Day Entry)
 * @param {Moment } date Текущая дата календаря
 * @param {IDateEntryFormat } start Время начала исключения
 * @returns {number[]} Список дней
 */
export function getWeekAndDayDaysEntry(
  date: Moment,
  start: IDateEntryFormat
): number[] {
  const currentMonth = date.clone().month() + 1;
  const days: number[] = [];

  // Проверка месяца
  if (checkSpecificMonth(start.month, currentMonth)) return [];

  const weeks = getWeeksEntry(start.week);
  const weekDays = getWeekDaysEntry(start.weekDay);

  weeks.forEach((week) => {
    let startOfPeriod: moment.Moment;
    let endOfPeriod: moment.Moment;

    // Последние 7 дней месяца
    if (week === 6) {
      startOfPeriod = date.clone().endOf("month").subtract(6, "days");
      endOfPeriod = date.clone().endOf("month");
    } else {
      // Стандартные недели (1-5) по календарным числам
      startOfPeriod = date
        .clone()
        .startOf("month")
        .add((week - 1) * 7, "days");
      endOfPeriod = startOfPeriod.clone().add(6, "days");
    }

    // Фильтрация дней
    let currentDay = startOfPeriod.clone();
    while (
      currentDay.isSameOrBefore(endOfPeriod) &&
      currentDay.month() + 1 === currentMonth
    ) {
      const dateNum = currentDay.date();
      if (
        weekDays.includes(currentDay.isoWeekday()) &&
        !days.includes(dateNum)
      ) {
        days.push(dateNum);
      }
      currentDay.add(1, "day");
    }
  });

  return days.sort((a, b) => a - b);
}

/**
 * Фильтр месяца по специфическим значениям
 * @param { number | string } month Начальный месяц события
 * @param { number } currentMonth Текущий месяц по календарю
 * @returns { Array<any> | null }
 */
export function checkSpecificMonth(
  month: number | string,
  currentMonth: number
): boolean {
  if (month === "**") return false;
  if (month === 13) return currentMonth % 2 !== 1; // Пропускаем четные месяцы
  if (month === 14) return currentMonth % 2 !== 0; // Пропускаем нечетные месяцы
  if (month !== currentMonth) return true;

  return false;
}

/**
 * Фильтр года по специфическим значениям
 * @param {number | string } year Начальный месяц события
 * @param {number} currentMonth Текущий месяц по календарю
 * @returns { Array<any> | null}
 */
export function checkSpecificYear(
  year: string | number,
  currentYear: number
): boolean {
  if (year === "****") return false;
  if (year !== currentYear) return true;

  return false;
}

/**
 * Форматирует дату в формате dDDMMYYYY
 * @param {IDateEntryFormat} date Объект даты
 * @returns {string} Строка в формате dDDMMYYYY
 */
export function formatDateEntry(date: IDateEntryFormat): string {
  const day = formatValue(date.day, 2);
  const month = formatValue(date.month, 2);
  const year = formatValue(date.year, 4);

  return `${date.weekDay}${day}${month}${year}`;
}

export function formatWeekDayEntry(date: IDateEntryFormat): string {
  const month = formatValue(date.month, 2);

  return `${month}${date.week}${date.weekDay}`;
}

/**
 * Форматирует значение в зависимости от типа и длины
 * @param {string | number } value Значение
 * @param {number} length Длина строки
 * @returns {string} Отформатированное значение
 */
export function formatValue(value: string | number, length: number): string {
  if (value === "*" || value === "**" || value === "****") return value;

  return String(value).padStart(length, "0");
}

/**
 * Получение списка недель для обработки задач
 * @param {string | number } week Номер недели
 * @returns {number[]}
 */
export function getWeeksEntry(week: string | number): number[] {
  if (week === "*") return [1, 2, 3, 4, 5]; // Все недели
  if (week === 6) return [6]; // Последняя неделя

  return [parseInt(week.toString())]; // Конкретная неделя
}

/**
 * Получение списка дней для обработки задач
 * @param {string | number } weekDay День недели
 * @returns {number[]}
 */
export function getWeekDaysEntry(weekDay: string | number): number[] {
  if (weekDay === "*") return [1, 2, 3, 4, 5, 6, 7]; // Все дни недели
  return [parseInt(weekDay.toString())]; // Конкретный день недели
}

export function formatEventValue(event: IEvent | IExclude, enumRef: Readonly<Record<number, string>>): string | number {
  if (event.dataType === 1) {
    return event.value === '1' || event.value === 1 ? 'True' : 'False';
  }
  if (event.dataType === 9) {
    return enumRef[event.value]
  }
  return event.value;
}